import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { IRForm } from "@/monitors/components/IRForm";
import {
  useMonitorOptions,
  useRunBacktest,
  type CompileResult,
  type BacktestResult,
} from "@/monitors/api";
import { writeStashedIR } from "@/monitors/hooks/useDraftIRStash";
import type { MonitorIR } from "@/monitors/schemas";

interface Props {
  compiled: CompileResult;
  onBack: () => void;
  onNext: (ir: MonitorIR) => void;
}

export function Step2Backtest({ compiled, onBack, onNext }: Props) {
  const [ir, setIR] = useState<MonitorIR>(compiled.ir);
  const [edited, setEdited] = useState(false);
  const backtest = useRunBacktest();
  const [result, setResult] = useState<BacktestResult | null>(null);
  // Audit I8: pull the categories + materials lists from the server, single
  // source of truth shared with the backend's compile prompt.
  const { data: options } = useMonitorOptions();

  const run = async () => {
    const r = await backtest.mutateAsync({ draftId: compiled.draft_id });
    setResult(r);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-4">
          <h3 className="font-semibold mb-3">Compiled understanding</h3>
          <IRForm
            value={ir}
            onChange={(v) => {
              setIR(v);
              setEdited(true);
              // Audit F1: persist to sessionStorage so a page reload at
              // step 2 or step 3 doesn't silently revert these edits to
              // the LLM-compiled original.
              writeStashedIR(compiled.draft_id, v);
            }}
            categories={options?.categories ?? []}
            materials={options?.materials ?? []}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Backtest (last 7 days)</h3>
            <Button onClick={run} disabled={backtest.isPending}>
              {backtest.isPending ? "Running…" : "Run backtest"}
            </Button>
          </div>
          {edited && (
            <p className="text-xs text-muted-foreground mb-3">
              Backtest uses the compiled IR; your edits apply when you Save.
            </p>
          )}
          {result && (
            <div className="space-y-4">
              <p className="text-sm">
                <strong>Would have fired {result.would_have_confirmed.length} time(s) in last 7 days</strong>
                {" — "}≈ {result.projected_rate_per_day} / day
              </p>
              <ul className="space-y-2">
                {result.would_have_confirmed.map((m) => (
                  <li key={m.cluster_id} className="border rounded p-2 text-sm">
                    <div className="font-medium">{m.title}</div>
                    <div className="text-xs text-muted-foreground italic">Why: {m.reason}</div>
                    <div className="text-xs">conf {m.confidence.toFixed(2)} · {new Date(m.first_seen_at).toLocaleString()}</div>
                  </li>
                ))}
              </ul>
              {result.candidates_only.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {result.candidates_only.length} additional candidates didn't pass the verify threshold.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>← Edit statement</Button>
        <Button onClick={() => onNext(ir)}>Save monitor →</Button>
      </div>
    </div>
  );
}
